// src/components/bids/wizard/steps/components/EngagementFilters.jsx

import React, { useMemo, useState } from 'react';
import {
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarIcon,
  XMarkIcon,
  TagIcon,
} from '@heroicons/react/24/outline';

const EngagementFilters = ({
  filters,
  realEngagements = [],  // full unfiltered list — used to derive filter options
  onUpdateFilter,
  isLoadingEngagements,
  onResetFilters,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const isCustomDateRange = filters?.timePeriod === 'custom';

  const hasActiveFilters =
    filters?.searchClient ||
    filters?.industry ||
    filters?.practiceArea ||
    filters?.tags?.length > 0 ||
    (filters?.timePeriod && filters.timePeriod !== 'all') ||
    filters?.startDate ||
    filters?.endDate ||
    filters?.excludeUnknown;

  // ── Derive filter options from actual data ────────────────────────
  const industryOptions = useMemo(() => {
    const values = realEngagements
      .map((e) => e.industry)
      .filter(Boolean);
    return [...new Set(values)].sort();
  }, [realEngagements]);

  const practiceAreaOptions = useMemo(() => {
    const values = realEngagements
      .map((e) => e.practice_area)
      .filter(Boolean);
    return [...new Set(values)].sort();
  }, [realEngagements]);

  const tagOptions = useMemo(() => {
    const all = realEngagements.flatMap((e) => e.tags || []).filter(Boolean);
    return [...new Set(all)].sort();
  }, [realEngagements]);

  // ── Tag toggle helper ─────────────────────────────────────────────
  const selectedTags = filters?.tags || [];

  const toggleTag = (tag) => {
    const current = filters?.tags || [];
    const next = current.includes(tag)
      ? current.filter((t) => t !== tag)
      : [...current, tag];
    onUpdateFilter('tags', next);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FunnelIcon className="w-4 h-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">Filter Engagements</h3>
          {hasActiveFilters && !isExpanded && (
            <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              Active
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={(e) => { e.stopPropagation(); onResetFilters?.(); }}
              className="px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
              Clear all
            </button>
          )}
          <button className="text-gray-400 hover:text-gray-600 transition-colors">
            {isExpanded
              ? <ChevronUpIcon className="w-4 h-4" />
              : <ChevronDownIcon className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 space-y-4 pt-4">

          {/* Row 1: Search, Industry, Practice Area, Time Period */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

            {/* Search */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Search</label>
              <input
                type="text"
                value={filters?.searchClient || ''}
                onChange={(e) => onUpdateFilter('searchClient', e.target.value)}
                placeholder="Name, client..."
                disabled={isLoadingEngagements}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-100"
              />
            </div>

            {/* Industry — derived from data */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Industry</label>
              <select
                value={filters?.industry || ''}
                onChange={(e) => onUpdateFilter('industry', e.target.value)}
                disabled={isLoadingEngagements}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-100"
              >
                <option value="">All Industries</option>
                {industryOptions.map((ind) => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>

            {/* Practice Area — derived from data */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Practice Area</label>
              <select
                value={filters?.practiceArea || ''}
                onChange={(e) => onUpdateFilter('practiceArea', e.target.value)}
                disabled={isLoadingEngagements || practiceAreaOptions.length === 0}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-100"
              >
                <option value="">All Practice Areas</option>
                {practiceAreaOptions.map((pa) => (
                  <option key={pa} value={pa}>{pa}</option>
                ))}
              </select>
            </div>

            {/* Time Period */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Time Period</label>
              <select
                value={filters?.timePeriod || 'all'}
                onChange={(e) => {
                  onUpdateFilter('timePeriod', e.target.value);
                  if (e.target.value !== 'custom') {
                    onUpdateFilter('startDate', null);
                    onUpdateFilter('endDate',   null);
                  }
                }}
                disabled={isLoadingEngagements}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-100"
              >
                <option value="all">All Time</option>
                <option value="last30">Last 30 Days</option>
                <option value="last90">Last 90 Days</option>
                <option value="last180">Last 6 Months</option>
                <option value="lastYear">Last Year</option>
                <option value="thisYear">This Year</option>
                <option value="lastTwoYears">Last 2 Years</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>

          {/* Row 2: Custom date range (conditional) */}
          {isCustomDateRange && (
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5" /> Start Date
                </label>
                <input
                  type="date"
                  value={filters?.startDate || ''}
                  onChange={(e) => onUpdateFilter('startDate', e.target.value)}
                  disabled={isLoadingEngagements}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5" /> End Date
                </label>
                <input
                  type="date"
                  value={filters?.endDate || ''}
                  onChange={(e) => onUpdateFilter('endDate', e.target.value)}
                  disabled={isLoadingEngagements}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100"
                />
              </div>
            </div>
          )}

          {/* Row 3: Tags multiselect — only shown if tags exist */}
          {tagOptions.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1">
                <TagIcon className="w-3.5 h-3.5" /> Tags
                {selectedTags.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                    {selectedTags.length} selected
                  </span>
                )}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {tagOptions.map((tag) => {
                  const isSelected = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      disabled={isLoadingEngagements}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        isSelected
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Row 4: Options */}
          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="excludeUnknown"
              checked={filters?.excludeUnknown || false}
              onChange={(e) => onUpdateFilter('excludeUnknown', e.target.checked)}
              disabled={isLoadingEngagements}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label htmlFor="excludeUnknown" className="text-xs text-gray-600 cursor-pointer select-none">
              Exclude "Unknown" clients
            </label>
          </div>

        </div>
      )}
    </div>
  );
};

export default EngagementFilters;